import React from 'react';
import { Star, Quote } from 'lucide-react';
import { testimonials } from '../data/mock';
import useReveal from '../hooks/useReveal';

const RESULTS_IMG =
  'https://images.unsplash.com/photo-1590666859933-7c8b7c6a66b8?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600';

const Testimonial = ({ t, idx }) => {
  const { ref, visible } = useReveal();
  return (
    <figure
      ref={ref}
      data-testid={`testimonial-${t.id}`}
      className={`reveal ${visible ? 'is-visible' : ''} relative bg-carbon border border-pewter/70 p-8 md:p-10 group hover:border-blush/60 transition-colors duration-500`}
      style={{ '--reveal-delay': `${(idx % 2) * 110}ms` }}
    >
      <Quote className="text-blush/70 mb-6" size={28} />
      <blockquote className="font-serif text-ivory text-lg md:text-xl leading-[1.5] mb-8">
        {t.text}
      </blockquote>
      <div className="hairline mb-5" />
      <figcaption className="flex items-center justify-between">
        <div>
          <p className="text-ivory text-sm tracking-[0.15em] uppercase">{t.name}</p>
          <p className="text-silverx text-[11px] tracking-[0.22em] uppercase mt-1">
            {t.role}
          </p>
        </div>
        <div className="flex gap-1">
          {[...Array(t.rating)].map((_, i) => (
            <Star key={i} className="text-blush fill-blush" size={13} />
          ))}
        </div>
      </figcaption>
    </figure>
  );
};

const Results = () => {
  const display = testimonials.slice(0, 4);
  const heroReveal = useReveal();

  return (
    <section id="results" data-testid="results-section" className="relative py-28 md:py-36 bg-onyx overflow-hidden">
      {/* Hero strip */}
      <div className="relative h-[44vh] min-h-[360px] max-h-[520px] mb-20 overflow-hidden">
        <img
          src={RESULTS_IMG}
          alt="Sculpted body editorial"
          className="absolute inset-0 w-full h-full object-cover editorial-img"
          loading="lazy"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(10,10,11,0.55) 0%, rgba(10,10,11,0.2) 45%, rgba(10,10,11,0.85) 100%)',
          }}
        />
        <div className="relative h-full flex items-end">
          <div
            ref={heroReveal.ref}
            className={`reveal ${heroReveal.visible ? 'is-visible' : ''} max-w-7xl mx-auto w-full px-5 sm:px-8 lg:px-12 pb-12 md:pb-16`}
          >
            <p className="eyebrow mb-5 chrome-rose">Transformation</p>
            <h2 className="font-serif text-ivory text-4xl sm:text-5xl lg:text-[64px] leading-[1.04] max-w-3xl text-balance">
              Visible Results.
              <br />
              <span className="brand-italic chrome-rose">Elevated</span> Confidence.
            </h2>
            <p className="text-platinum text-base md:text-lg max-w-xl mt-6 leading-relaxed">
              Our mission is to provide strategic, personalized treatments that help our
              clients feel supported, empowered, and visibly transformed.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Testimonials */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {display.map((t, idx) => (
            <Testimonial key={t.id} t={t} idx={idx} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Results;
